/**
 * ClipsPage — Browse detected clips/moments across one or more workspaces.
 *
 * Displays a grid of video clips (moments) with thumbnails and AI-generated
 * descriptions. Users can select one or multiple workspaces to see all
 * detected shots from their uploaded videos. Clips can be multi-selected
 * and added to existing or new buckets.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useLocation, Link, useNavigate } from 'react-router-dom'
import { Layers, Film, Clock, ChevronDown, ChevronLeft, ChevronRight, Plus, Check, X, FolderOpen, Package, Filter, Play, Hourglass, Eye } from 'lucide-react'
import ViewToggle from '../components/ViewToggle'
import toast from 'react-hot-toast'
import apiClient from '../services/api'
import ClustersTab from '../components/Collections/ClustersTab'
import BucketsTab from '../components/Collections/BucketsTab'
import AnalysisTab from '../components/Collections/AnalysisTab'
import MediaPreviewModal from '../components/MediaPreviewModal'
import { useMediaPreview } from '../hooks/useMediaPreview'
import { apiErrorDetail } from '../utils/dataValidator'

interface BucketBrief {
  id: number
  name: string
}

interface Project {
  id: string
  name: string
  asset_count: number
}

interface MomentItem {
  id: number
  asset_id: number | null
  scene_index: number
  start_time: number | null
  end_time: number | null
  title: string | null
  visual_description: string | null
  audio_description: string | null
  thumbnail_url: string | null
  tags: string[] | null
  notes: string | null
  created_time: string | null
  asset_filename: string | null
  asset_file_type: string | null
  asset_duration: number | null
}

function formatTime(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatDurationHMS(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '00:00:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '--'
  const d = new Date(dateStr)
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`
}

function formatTotalDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} sec`
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`
}

export default function ClipsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(
    searchParams.get('projects') ? searchParams.get('projects')!.split(',') : []
  )
  const [moments, setMoments] = useState<MomentItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showProjectPicker, setShowProjectPicker] = useState(false)
  const [buckets, setBuckets] = useState<BucketBrief[]>([])
  const location = useLocation()

  // Clip selection state
  const [selectedMomentIds, setSelectedMomentIds] = useState<Set<number>>(new Set())
  const [showBulkBucketPicker, setShowBulkBucketPicker] = useState(false)
  const [showNewBucketModal, setShowNewBucketModal] = useState(false)
  const [newBucketName, setNewBucketName] = useState('')
  const [newBucketDesc, setNewBucketDesc] = useState('')
  const [creatingBucket, setCreatingBucket] = useState(false)
  const { preview, closePreview, openFromMoment } = useMediaPreview()
  const navigate = useNavigate()

  // View & filter state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [filterText, setFilterText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const perPage = 12

  // Determine active tab from route
  const pathSegment = location.pathname.split('/').pop()
  const activeTab = (['clips', 'buckets', 'clusters', 'analysis'] as const).includes(pathSegment as 'clips' | 'buckets' | 'clusters' | 'analysis')
    ? (pathSegment as 'clips' | 'buckets' | 'clusters' | 'analysis')
    : 'clips'

  // Track whether initial auto-selection has occurred
  const didAutoSelect = useRef(false)

  // Fetch projects for the selector
  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true)
      const res = await apiClient.get('/projects/')
      setProjects(res.data.projects)

      // Auto-select all projects on first load only (if none selected via URL)
      if (!didAutoSelect.current && res.data.projects.length > 0) {
        didAutoSelect.current = true
        const fromUrl = searchParams.get('projects')
        if (!fromUrl) {
          const allIds = res.data.projects.map((p: Project) => p.id)
          setSelectedProjectIds(allIds)
          setSearchParams({ projects: allIds.join(',') })
        }
      }
    } catch {
      // Projects fetch failure is non-critical
    } finally {
      setProjectsLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams])

  useEffect(() => { fetchProjects() }, [fetchProjects])

  // Fetch moments and buckets when selected projects change
  const fetchMoments = useCallback(async () => {
    if (selectedProjectIds.length === 0) return
    try {
      setLoading(true)
      setError(null)
      const projectsParam = selectedProjectIds.join(',')
      const [momRes, buckRes] = await Promise.all([
        apiClient.get('/moments/', { params: { project_ids: projectsParam, limit: 100 } }),
        apiClient.get('/buckets/', { params: { project_ids: projectsParam } }),
      ])
      setMoments(momRes.data.moments)
      setTotal(momRes.data.total)
      setBuckets(buckRes.data.buckets.map((b: { id: number; name: string }) => ({ id: b.id, name: b.name })))
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to load clips'))
    } finally {
      setLoading(false)
    }
  }, [selectedProjectIds])

  useEffect(() => { fetchMoments() }, [fetchMoments])

  // Clear clip selection when projects change
  useEffect(() => { setSelectedMomentIds(new Set()) }, [selectedProjectIds])

  // --- Clip selection helpers ---

  const toggleMomentSelection = (id: number) => {
    setSelectedMomentIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAllMoments = () => {
    setSelectedMomentIds(new Set(filteredMoments.map(m => m.id)))
  }

  const clearSelection = () => {
    setSelectedMomentIds(new Set())
    setShowBulkBucketPicker(false)
    setShowNewBucketModal(false)
    setNewBucketName('')
    setNewBucketDesc('')
  }

  const openDraftCreation = useCallback((scope: {
    scope_type: string
    scope_ref_id: number
    label: string
    project_id: string
  }) => {
    const query = new URLSearchParams({
      project_id: scope.project_id,
      scope_type: scope.scope_type,
      scope_ref_id: String(scope.scope_ref_id),
    })
    navigate(`/create-ai-draft?${query.toString()}`)
  }, [navigate])

  // --- Bucket actions ---

  const addSelectedToBucket = async (bucketId: number) => {
    try {
      await apiClient.post(`/buckets/${bucketId}/moments`, { moment_ids: Array.from(selectedMomentIds) })
      toast.success(`Added ${selectedMomentIds.size} clip${selectedMomentIds.size !== 1 ? 's' : ''} to bucket`)
      clearSelection()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to add to bucket'))
    }
  }

  const createBucketWithSelected = async () => {
    if (!newBucketName.trim() || selectedProjectIds.length === 0) return
    try {
      setCreatingBucket(true)
      await apiClient.post(`/buckets/?project_id=${selectedProjectIds[0]}`, {
        name: newBucketName.trim(),
        description: newBucketDesc.trim() || null,
        moment_ids: Array.from(selectedMomentIds),
      })
      toast.success(`Created bucket "${newBucketName.trim()}" with ${selectedMomentIds.size} clip${selectedMomentIds.size !== 1 ? 's' : ''}`)
      clearSelection()
      fetchMoments() // Refresh bucket list
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to create bucket'))
    } finally {
      setCreatingBucket(false)
    }
  }

  // --- Project selector helpers ---

  const toggleProject = (id: string) => {
    const next = selectedProjectIds.includes(id)
      ? selectedProjectIds.filter(p => p !== id)
      : [...selectedProjectIds, id]
    setSelectedProjectIds(next)
    setSearchParams(next.length > 0 ? { projects: next.join(',') } : {})
  }

  const toggleAllProjects = () => {
    const allSelected = selectedProjectIds.length === projects.length
    const next = allSelected ? [] : projects.map(p => p.id)
    setSelectedProjectIds(next)
    setSearchParams(next.length > 0 ? { projects: next.join(',') } : {})
  }

  // Display text for the selector button
  const selectorText = projectsLoading
    ? 'Loading...'
    : selectedProjectIds.length === 0
      ? 'Select workspaces'
      : selectedProjectIds.length === projects.length && projects.length > 1
        ? 'All workspaces'
        : selectedProjectIds.length === 1
          ? projects.find(p => p.id === selectedProjectIds[0])?.name || 'Select workspaces'
          : `${selectedProjectIds.length} workspaces selected`

  // Group moments by asset to show "Clip X of Y"
  const assetMomentCounts: Record<number, number> = {}
  moments.forEach(m => {
    if (m.asset_id !== null) {
      assetMomentCounts[m.asset_id] = (assetMomentCounts[m.asset_id] || 0) + 1
    }
  })

  // Filter moments by text
  const filteredMoments = filterText.trim()
    ? moments.filter(m => {
        const text = filterText.toLowerCase()
        return (
          m.title?.toLowerCase().includes(text) ||
          m.visual_description?.toLowerCase().includes(text) ||
          m.asset_filename?.toLowerCase().includes(text)
        )
      })
    : moments

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredMoments.length / perPage))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedMoments = filteredMoments.slice((safePage - 1) * perPage, safePage * perPage)

  // Stats
  const distinctVideoCount = new Set(filteredMoments.map(m => m.asset_id).filter(Boolean)).size
  const totalClipDuration = filteredMoments.reduce((sum, m) => {
    if (m.start_time !== null && m.end_time !== null) return sum + (m.end_time - m.start_time)
    return sum
  }, 0)

  // Reset page when filter changes
  useEffect(() => { setCurrentPage(1) }, [filterText])

  const hasSelection = selectedMomentIds.size > 0
  return (
    <div className={`rq-page ${hasSelection ? 'pb-28' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="rq-title">Asset Views</h1>
          <p className="rq-subtitle">
            AI-reorganized assets for discovery and prep
          </p>
        </div>
      </div>

      {/* Workspace selector (multi-select) */}
      <div className="relative mb-6">
        <button
          onClick={() => setShowProjectPicker(!showProjectPicker)}
          className="flex items-center gap-2 px-4 py-2.5 rq-card rq-card-hover min-w-[200px]"
        >
          <Layers className="w-4 h-4 text-[var(--rq-text-soft)]" />
          <span className="flex-1 text-left truncate text-[var(--rq-text)]">{selectorText}</span>
          <ChevronDown className={`w-4 h-4 text-[var(--rq-text-soft)] transition-transform ${showProjectPicker ? 'rotate-180' : ''}`} />
        </button>

        {showProjectPicker && (
          <div className="absolute z-20 mt-1 w-full max-w-sm rq-card shadow-xl overflow-hidden">
            {/* All workspaces toggle */}
            {projects.length > 1 && (
              <button
                onClick={toggleAllProjects}
                className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-3 border-b border-white/10"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  selectedProjectIds.length === projects.length
                    ? 'bg-[var(--btn)] border-[var(--btn)]'
                    : 'border-white/30'
                }`}>
                  {selectedProjectIds.length === projects.length && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-[var(--rq-text)] text-sm font-medium">All Workspaces</span>
              </button>
            )}
            {projects.map(p => (
              <button
                key={p.id}
                onClick={() => toggleProject(p.id)}
                className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-3"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  selectedProjectIds.includes(p.id)
                    ? 'bg-[var(--btn)] border-[var(--btn)]'
                    : 'border-white/30'
                }`}>
                  {selectedProjectIds.includes(p.id) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-[var(--rq-text)] text-sm font-medium truncate flex-1">{p.name}</span>
                <span className="text-xs text-[var(--rq-text-soft)] ml-2">{p.asset_count} asset{p.asset_count !== 1 ? 's' : ''}</span>
              </button>
            ))}
            {projects.length === 0 && (
              <div className="px-4 py-3 text-sm text-[var(--rq-text-soft)]">No workspaces yet</div>
            )}
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-white/10">
        {([
          { key: 'clips', label: 'Clips', path: '/collections/clips' },
          { key: 'buckets', label: 'Buckets', path: '/collections/buckets' },
          { key: 'clusters', label: 'Clusters', path: '/collections/clusters' },
          { key: 'analysis', label: 'Analysis', path: '/collections/analysis' },
        ] as const).map(tab => (
          <Link
            key={tab.key}
            to={tab.path + location.search}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'text-[var(--rq-text)] border-[var(--btn)]'
                : 'text-[var(--rq-text-soft)] border-transparent hover:text-[var(--rq-text)]'
            }`}
          >
            {tab.label}
          </Link>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'clips' && (
        <>
      {selectedProjectIds.length === 0 ? (
        <div className="text-center py-16">
          <Layers className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">Select a workspace</h2>
          <p className="text-[var(--rq-text-soft)]">Choose a workspace above to browse its clips</p>
        </div>
      ) : loading ? (
        <div className="text-center py-16 text-[var(--rq-text-soft)]">Loading clips...</div>
      ) : error ? (
        <div className="text-center py-16 text-red-400">{error}</div>
      ) : moments.length === 0 ? (
        <div className="text-center py-16">
          <Film className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No clips yet</h2>
          <p className="text-[var(--rq-text-soft)] max-w-md mx-auto">
            Upload videos to your workspace, then click &ldquo;Analyze&rdquo; on each video
            to detect shots and generate clips with thumbnails.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Filter bar */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-xs w-full">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rq-text-soft)]" />
              <input
                type="text"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                placeholder="Filter by clip title"
                className="w-full rq-input-dark rounded-xl pl-9 pr-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[var(--rq-text-soft)]">
                Sort by <span className="text-[var(--rq-text-soft)]">Recent upload</span>
              </span>
              <ViewToggle mode={viewMode} onChange={setViewMode} />
            </div>
          </div>

          {/* Stats bar */}
          <div className="flex items-center gap-4 text-sm text-[var(--rq-text-soft)]">
            <span className="flex items-center gap-1.5">
              <Play className="w-3.5 h-3.5" />
              {filteredMoments.length} / {total} clips
            </span>
            <span className="text-[var(--rq-text-soft)]">&middot;</span>
            <span className="flex items-center gap-1.5">
              <Hourglass className="w-3.5 h-3.5" />
              {formatTotalDuration(totalClipDuration)}
            </span>
            {distinctVideoCount > 0 && (
              <>
                <span className="text-[var(--rq-text-soft)]">&middot;</span>
                <span>{distinctVideoCount} video{distinctVideoCount !== 1 ? 's' : ''}</span>
              </>
            )}
          </div>

          {/* List view */}
          {viewMode === 'list' && (
            <div>
              {/* Table header */}
              <div className="grid grid-cols-[40px_1fr_140px_140px] items-center gap-4 px-2 py-3 border-b border-white/10 text-sm font-medium text-[var(--rq-text-soft)]">
                <div className="flex justify-center">
                  <button
                    onClick={hasSelection && selectedMomentIds.size === filteredMoments.length ? clearSelection : selectAllMoments}
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      hasSelection && selectedMomentIds.size === filteredMoments.length
                        ? 'bg-[var(--btn)] border-[var(--btn)]'
                        : 'border-white/30 hover:border-white/45'
                    }`}
                  >
                    {hasSelection && selectedMomentIds.size === filteredMoments.length && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </button>
                </div>
                <span>Clip title</span>
                <span className="text-right">Clip duration</span>
                <span className="text-right">Date uploaded</span>
              </div>

              {/* Table rows */}
              {paginatedMoments.map(moment => {
                const isSelected = selectedMomentIds.has(moment.id)
                const clipDuration = moment.start_time !== null && moment.end_time !== null
                  ? moment.end_time - moment.start_time
                  : null
                return (
                  <div
                    key={moment.id}
                    onClick={() => toggleMomentSelection(moment.id)}
                    className={`grid grid-cols-[40px_1fr_140px_140px] items-center gap-4 px-2 py-4 border-b border-white/5 cursor-pointer transition-colors group ${
                      isSelected ? 'bg-white/10' : 'hover:bg-white/5'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className="flex justify-center">
                      <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-[var(--btn)] border-[var(--btn)]'
                          : 'border-white/20 group-hover:border-white/45'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>

                    {/* Thumbnail + title */}
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-24 h-14 rounded-lg bg-black/40 overflow-hidden flex-shrink-0">
                        {moment.thumbnail_url ? (
                          <div className="relative w-full h-full">
                            <img
                              src={moment.thumbnail_url}
                              alt={moment.title || 'Clip thumbnail'}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                openFromMoment(moment)
                              }}
                              className="absolute top-1 right-1 w-5 h-5 rounded bg-black/70 text-white/90 hover:text-white hover:bg-black/85 flex items-center justify-center transition-colors"
                              title="Preview"
                            >
                              <Eye className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-[var(--rq-text)] truncate">
                          {moment.visual_description || moment.title || moment.asset_filename || 'Untitled clip'}
                        </p>
                        {moment.asset_filename && (moment.visual_description || moment.title) && (
                          <p className="text-xs text-[var(--rq-text-soft)] truncate mt-0.5">{moment.asset_filename}</p>
                        )}
                      </div>
                    </div>

                    {/* Duration */}
                    <span className="text-sm text-[var(--rq-text-soft)] text-right tabular-nums">
                      {formatDurationHMS(clipDuration)}
                    </span>

                    {/* Date */}
                    <span className="text-sm text-[var(--rq-text-soft)] text-right tabular-nums">
                      {formatDate(moment.created_time)}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Grid view */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {paginatedMoments.map(moment => {
                const isSelected = selectedMomentIds.has(moment.id)
                return (
                  <div
                    key={moment.id}
                    onClick={() => toggleMomentSelection(moment.id)}
                    className={`rq-card border overflow-hidden transition-all group cursor-pointer ${
                      isSelected
                        ? 'border-[var(--btn)] ring-1 ring-[var(--btn)]'
                        : 'hover:border-white/20'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-black/40">
                      {moment.thumbnail_url ? (
                        <>
                          <img
                            src={moment.thumbnail_url}
                            alt={moment.title || 'Clip thumbnail'}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              openFromMoment(moment)
                            }}
                            className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white/90 hover:text-white hover:bg-black/85 flex items-center justify-center transition-colors"
                            title="Preview"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-10 h-10 text-[var(--rq-text-soft)]" />
                        </div>
                      )}

                      {/* Selection checkbox */}
                      <div className={`absolute top-2 left-2 w-5 h-5 rounded border flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-[var(--btn)] border-[var(--btn)]'
                          : 'border-white/50 bg-black/30 opacity-0 group-hover:opacity-100'
                      }`}>
                        {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                      </div>

                      {/* Clip badge */}
                      <div className="absolute top-2 right-2 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                        Clip {moment.scene_index + 1} of {moment.asset_id !== null ? assetMomentCounts[moment.asset_id] || '?' : '?'}
                      </div>

                      {/* Duration badge */}
                      {moment.start_time !== null && moment.end_time !== null && (
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                          <Clock className="w-3 h-3" />
                          {formatTime(moment.end_time - moment.start_time)}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-3">
                      <p className="text-sm text-[var(--rq-text-soft)] line-clamp-2 leading-relaxed">
                        {moment.visual_description || moment.title || 'No description'}
                      </p>
                      {moment.asset_filename && (
                        <p className="text-xs text-[var(--rq-text-soft)] truncate mt-2">
                          {moment.asset_filename}
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="p-2 rounded-lg text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] disabled:opacity-30 disabled:hover:text-[var(--rq-text-soft)] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {(() => {
                const pages: (number | '...')[] = []
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i)
                } else {
                  pages.push(1)
                  if (safePage > 3) pages.push('...')
                  for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) pages.push(i)
                  if (safePage < totalPages - 2) pages.push('...')
                  pages.push(totalPages)
                }
                return pages.map((page, idx) =>
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-sm text-[var(--rq-text-soft)]">...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                        page === safePage
                          ? 'bg-white/20 text-[var(--rq-text)]'
                          : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )
              })()}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="p-2 rounded-lg text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] disabled:opacity-30 disabled:hover:text-[var(--rq-text-soft)] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <span className="text-sm text-[var(--rq-text-soft)] ml-2">{perPage} / page</span>
            </div>
          )}
        </div>
      )}
        </>
      )}

      {activeTab === 'buckets' && (
        <BucketsTab projectIds={selectedProjectIds} onGenerateDraft={openDraftCreation} />
      )}

      {activeTab === 'clusters' && (
        <ClustersTab projectIds={selectedProjectIds} onGenerateDraft={openDraftCreation} />
      )}

      {activeTab === 'analysis' && (
        <AnalysisTab projectIds={selectedProjectIds} />
      )}

      {/* Floating action bar when clips are selected */}
      {activeTab === 'clips' && hasSelection && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 rq-card rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4">
          {/* Selection count */}
          <span className="text-sm text-[var(--rq-text)] font-medium whitespace-nowrap">
            {selectedMomentIds.size} clip{selectedMomentIds.size !== 1 ? 's' : ''} selected
          </span>

          <div className="w-px h-6 bg-white/20" />

          {/* Add to existing bucket */}
          <div className="relative">
            <button
              onClick={() => setShowBulkBucketPicker(!showBulkBucketPicker)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors bg-white/10 hover:bg-white/20 text-[var(--rq-text)]"
            >
              <FolderOpen className="w-4 h-4" />
              Add to Bucket
            </button>
            {showBulkBucketPicker && (
              <div className="absolute bottom-full mb-2 left-0 rq-card rounded-xl shadow-xl overflow-hidden min-w-[200px]">
                {buckets.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-[var(--rq-text-soft)]">No buckets yet</div>
                ) : (
                  buckets.map(b => (
                    <button
                      key={b.id}
                      onClick={() => addSelectedToBucket(b.id)}
                      className="w-full px-4 py-2.5 text-left text-sm text-[var(--rq-text-soft)] hover:bg-white/5 hover:text-[var(--rq-text)] transition-colors"
                    >
                      {b.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Open New Bucket modal */}
          <button
            onClick={() => {
              setShowNewBucketModal(true)
              setShowBulkBucketPicker(false)
            }}
            className="px-3 py-1.5 text-sm rq-btn-primary"
          >
            <Plus className="w-4 h-4" />
            New Bucket
          </button>

          <div className="w-px h-6 bg-white/20" />

          {/* Clear selection */}
          <button
            onClick={clearSelection}
            className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
            title="Clear selection"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Create New Bucket Modal */}
      {showNewBucketModal && (
        <div className="rq-modal-overlay">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => { setShowNewBucketModal(false); setNewBucketName(''); setNewBucketDesc('') }}
          />

          {/* Modal */}
          <div className="relative rq-modal-card !max-w-md mx-4">
            {/* Icon */}
            <Package className="w-10 h-10 text-[var(--rq-text-soft)] mb-4" />

            {/* Title */}
            <h2 className="text-xl font-bold text-[var(--rq-text)] mb-5">Create New Bucket</h2>

            {/* Clip count banner */}
            <div className="flex items-center gap-3 bg-emerald-900/30 border border-emerald-700/40 rounded-xl px-4 py-3 mb-6">
              <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <Plus className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm text-emerald-200">
                {selectedMomentIds.size} clip{selectedMomentIds.size !== 1 ? 's' : ''} will be added to this bucket
              </span>
            </div>

            {/* Bucket Name */}
            <label className="block text-sm font-medium text-[var(--rq-text-soft)] mb-2">Bucket Name</label>
            <div className="relative mb-5">
              <input
                autoFocus
                value={newBucketName}
                onChange={e => setNewBucketName(e.target.value)}
                placeholder="Outdoor talking head shots"
                className="w-full rq-input-dark rounded-xl px-4 py-3 pr-10 text-sm"
                onKeyDown={e => { if (e.key === 'Enter' && newBucketName.trim()) createBucketWithSelected() }}
              />
              {newBucketName && (
                <button
                  onClick={() => setNewBucketName('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Description */}
            <label className="block text-sm font-medium text-[var(--rq-text-soft)] mb-2">Description (Optional)</label>
            <textarea
              value={newBucketDesc}
              onChange={e => setNewBucketDesc(e.target.value)}
              placeholder="Add a description for this bucket"
              rows={3}
              className="w-full rq-input-dark rounded-xl px-4 py-3 text-sm resize-none mb-6"
            />

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowNewBucketModal(false); setNewBucketName(''); setNewBucketDesc('') }}
                className="rq-btn-ghost px-5 py-2.5 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={createBucketWithSelected}
                disabled={!newBucketName.trim() || creatingBucket}
                className="rq-btn-primary px-5 py-2.5 disabled:opacity-40 text-sm"
              >
                {creatingBucket ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MediaPreviewModal preview={preview} onClose={closePreview} />

    </div>
  )
}
